/**
 * Password reset, both halves.
 *
 * `/reset` asks for an address; `/reset?token=…` — the link from the email —
 * asks for a new password. One screen because it is one idea, and because a
 * person following a link should not first have to work out which of two pages
 * they are on.
 *
 * The request half always reports the same thing whether or not the address has
 * an account. That is not vagueness for its own sake: a reset form that
 * confirms an address is a way to find out who has registered.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { redeemPasswordReset, requestPasswordReset } from '../net/auth.js';

const MIN_PASSWORD = 8;

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <ChooseNewPassword token={token} /> : <AskForLink />;
}

function AskForLink() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    try {
      await requestPasswordReset(email);
    } catch {
      // Even a network failure shows the same confirmation. Anything else
      // would let this form answer "does this address exist".
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="page narrow">
        <h1 className="page-title">Check your email</h1>
        <p className="field-help">
          If that address has an account, a reset link is on its way. It works once and
          expires in an hour.
        </p>
        <Link className="big-button" to="/login">
          Back to log in
        </Link>
      </div>
    );
  }

  return (
    <form className="page narrow" onSubmit={submit}>
      <h1 className="page-title">Reset your password</h1>

      <label className="field">
        <span className="field-label">Email</span>
        <input
          className="text-input"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
      </label>

      <button type="submit" className="big-button primary" disabled={busy}>
        {busy ? 'Sending…' : 'Send me a link'}
      </button>

      <div className="page-links">
        <Link className="text-button" to="/login">
          Back
        </Link>
      </div>
    </form>
  );
}

function ChooseNewPassword({ token }: { token: string }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = password.length >= MIN_PASSWORD && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      const result = await redeemPasswordReset(token, password);
      if (result.ok) {
        // Straight to log in rather than signing them in automatically: proving
        // the new password works now is better than discovering it does not
        // later.
        navigate('/login');
        return;
      }
      setError(result.message ?? 'That link has expired or has already been used.');
    } catch {
      setError('Could not reach the server. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page narrow" onSubmit={submit}>
      <h1 className="page-title">Choose a new password</h1>

      <label className="field">
        <span className="field-label">New password</span>
        <input
          className="text-input"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
        />
        {password.length > 0 && password.length < MIN_PASSWORD ? (
          <span className="field-error">At least {MIN_PASSWORD} characters.</span>
        ) : null}
      </label>

      {error ? <p className="banner error">{error}</p> : null}

      <button type="submit" className="big-button primary" disabled={!ready}>
        {busy ? 'Saving…' : 'Save password'}
      </button>

      <div className="page-links">
        <Link className="text-button" to="/reset">
          Send me a new link
        </Link>
      </div>
    </form>
  );
}
